import type { RenderingOf, RequestOf } from "../../../access/types.ts";
import { Access, Delegate } from "../../../access/types.ts";
import { AwsRdsAccesses } from "../aws-rds/accesses.ts";
import { GcloudAccesses } from "../gcloud/accesses.ts";

const principalTypeArg = {
  description: "The type of principal that should receive access",
  label: "Principal type",
  type: "input",
  choices: ["user", "group", "service-account", "wif-identity", "wif-user"],
  supportedCommands: ["grant", "request"],
  optional: true,
  hidden: true,
} as const;

export const DbAccesses = {
  role: Access({
    label: "Role",
    description: "A database role",
    maturity: "preview",
    delegation: {
      "aws-rds": Delegate({
        resource: "aws-rds",
        key: "resource" as const,
        accesses: AwsRdsAccesses,
      }),
      "gsm-static": Delegate({
        resource: "gcloud",
        key: "resource" as const,
        accesses: GcloudAccesses,
      }),
    },
    generated: {
      // The enclosing role name
      name: { type: "string" },
    },
    parameters: {
      instance: {
        type: "static",
        label: "The instance (or cluster) to access",
        optional: true,
      },
      instanceId: {
        type: "generated",
        label: "The instance (or cluster) to access",
      },
      hostname: {
        type: "generated",
        label: "The hostname of the target database",
      },
      database: {
        type: "static",
        label: "The database to use",
        description:
          "May be omitted if your organization has a default database",
        optional: true,
      },
      name: {
        type: "dynamic",
        label: "The requested role",
      },
      dbUser: {
        type: "generated",
        label: "The database user to create",
      },
      "row-level": {
        type: "static",
        label: "Row-level access policy",
        optional: true,
      },
      "row-level-value": {
        type: "input",
        label: "Row-level binding value",
        description:
          "Value to bind to '?' in the row-level policy expressions. Defaults to the database username.",
        optional: true,
      },
      "principal-type": principalTypeArg,
    },
    render: {
      locator: "instance",
      policy: "name",
      extra: ["row-level", "row-level-value"],
    } as const,
  }),
};

export const newDbRendering: (arch: string) => DbRendering = (
  arch: string
) => ({
  role: {
    locator: "instanceId",
    name: "name",
    instructions: (request) => {
      const { permission, delegation } = request;
      if (!("aws-rds" in delegation)) return undefined;
      const prefix =
        "👉 To connect to your instance, please run the following command in the P0 CLI, then follow the printed instructions.";
      const command = `\`p0 aws rds generate-db-auth-token --role ${permission.name} --arch ${arch} --instance ${permission.instanceId}\``;
      return prefix + "\n\n" + command;
    },
  },
});

export type DbAccesses = typeof DbAccesses;
export type DbRendering = RenderingOf<DbAccesses>;

export type DbRoleAccess = DbAccesses["role"];
export type DbRoleRequest = RequestOf<DbRoleAccess>;
